import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// Shared layout for the public-facing legal/support pages (Support, Terms,
// Refund Policy). These render outside the auth-gated app so they're
// accessible to logged-out users and to Apple's app review team.
//
// Composition: simple branded header → content card with markdown → small
// legal-link footer. All three pages share this shell so the header/footer
// stay consistent across them; only the title + body markdown differ.

type Props = {
  // Page <title> — set on document.title for SEO + browser tab.
  documentTitle: string
  // Rendered headline. Pre-formatted JSX so callers can do italic-last-word
  // patterns ("PantryIO Support" with "Support" italicized) without us
  // re-implementing tail-extraction here.
  heading: ReactNode
  // Optional muted italic line directly below the heading — used for
  // "Last updated: ..." on Terms and Refund Policy.
  meta?: ReactNode
  // Markdown body. The renderer handles links, headings, lists, and tables
  // (via remark-gfm). Components map below applies our type scale + colors.
  markdown: string
}

export function StaticPageLayout({ documentTitle, heading, meta, markdown }: Props) {
  useEffect(() => { document.title = documentTitle }, [documentTitle])

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">

      {/* ── Header ────────────────────────────────────────────────────── */}
      <header className="px-6 py-4 border-b border-border-sand">
        <Link to="/" className="inline-flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-[9px] bg-forest text-forest-ink font-serif italic text-[16px] font-semibold leading-none">
            P
          </span>
          <span className="font-serif text-[15px] font-medium text-ink italic">Pantry</span>
        </Link>
      </header>

      {/* ── Content card ──────────────────────────────────────────────── */}
      <main className="flex-1 px-6 py-8 sm:py-16">
        <div className="max-w-[720px] mx-auto">
          <article className="bg-paper border border-border-sand rounded-2xl p-5 sm:p-8">
            <h1 className="font-serif text-[28px] sm:text-[36px] text-ink leading-[1.15] tracking-[-0.015em] mb-2">
              {heading}
            </h1>
            {meta && (
              <p className="font-serif italic text-[14px] text-ink-muted mb-6 leading-[1.5]">
                {meta}
              </p>
            )}

            <div className="static-prose">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({ children }) => (
                    <h2 className="font-serif text-[24px] text-ink mt-8 mb-3 leading-[1.2] tracking-[-0.01em]">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-[18px] font-semibold text-ink mt-6 mb-2 leading-[1.3]">
                      {children}
                    </h3>
                  ),
                  p: ({ children }) => (
                    <p className="text-[16px] text-ink leading-[1.6] mb-4">
                      {children}
                    </p>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc pl-6 mb-4 text-[16px] text-ink leading-[1.6]">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal pl-6 mb-4 text-[16px] text-ink leading-[1.6]">
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="mb-1.5">{children}</li>
                  ),
                  a: ({ href, children }) => {
                    // Internal links → React Router (no full reload), external → plain anchor.
                    const isInternal = !!href && href.startsWith('/')
                    if (isInternal) {
                      return (
                        <Link
                          to={href!}
                          className="text-terracotta underline-offset-4 hover:underline decoration-terracotta/40"
                        >
                          {children}
                        </Link>
                      )
                    }
                    return (
                      <a
                        href={href}
                        className="text-terracotta underline-offset-4 hover:underline decoration-terracotta/40"
                      >
                        {children}
                      </a>
                    )
                  },
                  strong: ({ children }) => (
                    <strong className="font-semibold text-ink">{children}</strong>
                  ),
                  hr: () => <hr className="my-8 border-border-sand" />,
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-2 border-border-sand pl-4 italic text-ink-muted my-4">
                      {children}
                    </blockquote>
                  ),
                  code: ({ children }) => (
                    <code className="font-mono text-[14px] bg-cream-warm px-1.5 py-0.5 rounded">
                      {children}
                    </code>
                  ),
                }}
              >
                {markdown}
              </ReactMarkdown>
            </div>
          </article>
        </div>
      </main>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer className="px-6 py-6 border-t border-border-sand">
        <div className="max-w-[720px] mx-auto text-center">
          <nav className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 text-[13px] text-ink-muted mb-2">
            <Link to="/privacy"        className="hover:text-ink underline-offset-2 hover:underline">Privacy</Link>
            <span className="text-ink-muted/50">·</span>
            <Link to="/terms"          className="hover:text-ink underline-offset-2 hover:underline">Terms</Link>
            <span className="text-ink-muted/50">·</span>
            <Link to="/refund-policy"  className="hover:text-ink underline-offset-2 hover:underline">Refund Policy</Link>
            <span className="text-ink-muted/50">·</span>
            <Link to="/support"        className="hover:text-ink underline-offset-2 hover:underline">Support</Link>
          </nav>
          <p className="text-[12px] text-ink-muted/80">
            © 2026 PantryIO. All rights reserved.
          </p>
        </div>
      </footer>

    </div>
  )
}
